import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Check } from 'lucide-react';
import { animalData } from '../../data/animals';
import { useCart } from '../../context/CartContext';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

function ExtraCard({ animal, index }) {
  const { addItem } = useCart();
  const [includeCamelRides, setIncludeCamelRides] = useState(false);
  const [includeExtraFlamingos, setIncludeExtraFlamingos] = useState(false);
  const [extraFlamingoCount, setExtraFlamingoCount] = useState(1);

  const handleAdd = () => {
    addItem({ id: animal.id, name: animal.name, description: animal.description, price: animal.price, category: animal.category, image: animal.image, hours: 1 });
    if (animal.hasRideAddon && includeCamelRides) {
      addItem({ id: 'camel-rides', name: 'Camel Rides Add-On', price: 1000, category: 'extraordinary', hours: 1 });
    }
    if (animal.hasFlamingoAddon && includeExtraFlamingos && extraFlamingoCount > 0) {
      addItem({ id: `extra-flamingos-${Date.now()}`, name: `Additional Flamingos (${extraFlamingoCount})`, price: 1500 * extraFlamingoCount, category: 'extraordinary', hours: 1 });
    }
    setIncludeCamelRides(false);
    setIncludeExtraFlamingos(false);
    setExtraFlamingoCount(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group"
      data-testid={`card-${animal.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden mb-4">
        <img src={animal.image} alt={animal.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 border border-white/10">
          <span className="text-gold text-sm font-medium">{fmt(animal.price)}</span>
        </div>
      </div>

      <h3 className="font-display text-xl md:text-2xl text-white group-hover:text-gold transition-colors duration-300 mb-2">{animal.name}</h3>
      <p className="text-white/50 text-sm mb-3 leading-relaxed">{animal.description}</p>

      {animal.hasRideAddon && (
        <div className="flex items-center gap-2 mb-3 cursor-pointer group/cb" onClick={() => setIncludeCamelRides(!includeCamelRides)}>
          <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${includeCamelRides ? 'bg-gold border-gold' : 'border-white/30 group-hover/cb:border-gold/50'}`}>
            {includeCamelRides && <Check className="w-3 h-3 text-background" />}
          </div>
          <span className="text-white/70 text-xs">Add Camel Rides (+$1,000)</span>
        </div>
      )}

      {animal.hasFlamingoAddon && (
        <div className="mb-3">
          <div className="flex items-center gap-2 cursor-pointer group/cb" onClick={() => setIncludeExtraFlamingos(!includeExtraFlamingos)}>
            <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${includeExtraFlamingos ? 'bg-gold border-gold' : 'border-white/30 group-hover/cb:border-gold/50'}`}>
              {includeExtraFlamingos && <Check className="w-3 h-3 text-background" />}
            </div>
            <span className="text-white/70 text-xs">Add Extra Flamingos (+$1,500 each)</span>
          </div>
          {includeExtraFlamingos && (
            <div className="flex items-center gap-2 mt-2 ml-7">
              <button onClick={() => setExtraFlamingoCount(Math.max(1, extraFlamingoCount - 1))} className="w-6 h-6 flex items-center justify-center border border-white/20 text-white/50 hover:text-white"><Minus className="w-3 h-3" /></button>
              <span className="text-white text-sm w-6 text-center">{extraFlamingoCount}</span>
              <button onClick={() => setExtraFlamingoCount(extraFlamingoCount + 1)} className="w-6 h-6 flex items-center justify-center border border-white/20 text-white/50 hover:text-white"><Plus className="w-3 h-3" /></button>
              <span className="text-gold text-xs font-medium ml-2">+${(1500 * extraFlamingoCount).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      <button
        data-testid={`add-${animal.id}`}
        onClick={handleAdd}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-background text-xs font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors duration-300"
      >
        <Plus className="w-3 h-3" /> Add to Estimate
      </button>
    </motion.div>
  );
}

export function ExtraordinarySection() {
  const { row1, row2, row3 } = animalData.extraordinaryAnimals;

  return (
    <section data-testid="extraordinary-section" className="py-24 md:py-32 px-6 md:px-12 bg-background">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">One-of-a-Kind Encounters</p>
          <h2 className="font-display text-4xl md:text-6xl text-white uppercase tracking-wide mb-3">
            Extraordinary Animals
          </h2>
          <p className="text-white/50 text-base md:text-lg">Get up close & personal with these magnificent creatures.</p>
        </motion.div>

        {/* Row 1: Giraffe, Zebra, Giraffe+Zebra */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {row1.map((a, i) => <ExtraCard key={a.id} animal={a} index={i} />)}
        </div>

        {/* Row 2: Camel, Flamingos, Elephant */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {row2.map((a, i) => <ExtraCard key={a.id} animal={a} index={i} />)}
        </div>

        {/* Row 3: Brown Horse, White Horse, Kangaroo */}
        <div className="grid md:grid-cols-3 gap-8">
          {row3.map((a, i) => <ExtraCard key={a.id} animal={a} index={i} />)}
        </div>
      </div>
    </section>
  );
}
